import { Component } from '@theme/component';
import { ThemeEvents, StringingUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';
import { readCustomVariantMap, findCustomCounterpart, normalizeVariantId } from '@theme/custom-variant-map';

const FOCUSABLE_ELEMENTS_SELECTOR =
  'a[href], area[href], button, input:not([type="hidden"]), select, textarea, iframe, object, embed, [contenteditable="true"], [tabindex]';

/**
 * @typedef {object} TennisStringingRefs
 * @property {HTMLInputElement} enableCheckbox
 * @property {HTMLElement} configPanel
 * @property {HTMLSelectElement} stringModelSelect
 * @property {HTMLSelectElement} tensionMainsSelect
 * @property {HTMLSelectElement} tensionCrossesSelect
 * @property {HTMLInputElement} stencilCheckbox
 * @property {HTMLButtonElement} resetButton
 * @property {HTMLInputElement} propertyShipWithStrings
 * @property {HTMLInputElement} propertyStringModel
 * @property {HTMLInputElement} propertyStringSku
 * @property {HTMLInputElement} propertyRacketSku
 * @property {HTMLInputElement} propertyStockRacketSku
 * @property {HTMLInputElement} propertyTensionMains
 * @property {HTMLInputElement} propertyTensionCrosses
 * @property {HTMLInputElement} propertyStencilIncluded
 * @property {HTMLInputElement} propertyStringVariantId
 * @property {HTMLInputElement} propertyStringAddOnPriceCents
 */

/**
 * @typedef {object} StringingCartSubmission
 * @property {boolean} enabled
 * @property {boolean} addStringLineItem
 * @property {Record<string, string>} properties
 * @property {string} [stringVariantId]
 * @property {string} [customStrungVariantId]
 * @property {number} stringAddOnPrice
 */

/**
 * @extends {Component<TennisStringingRefs>}
 */
class TennisStringingOptions extends Component {
  /** @type {boolean} */
  #enabled = false;
  /** @type {MutationObserver | null} */
  #panelFocusObserver = null;

  connectedCallback() {
    super.connectedCallback();

    const section = this.closest('.shopify-section, dialog');
    section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);

    const enableCheckbox = this.refs.enableCheckbox;
    if (enableCheckbox instanceof HTMLInputElement) {
      this.#enabled = enableCheckbox.checked;
    }

    this.#bindToggleAccessibility();
    this.#observePanelFocusability();
    this.#ensureValidStringSelection();
    this.#syncControlsEnabled();
    this.#syncPropertyFields();
    this.#dispatchStringingUpdate();
  }

  #bindToggleAccessibility() {
    const enableCheckbox = this.refs.enableCheckbox;
    const { configPanel } = this.refs;

    if (enableCheckbox instanceof HTMLInputElement && configPanel?.id) {
      enableCheckbox.setAttribute('aria-controls', configPanel.id);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const section = this.closest('.shopify-section, dialog');
    section?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.#panelFocusObserver?.disconnect();
    this.#panelFocusObserver = null;
  }

  /**
   * @param {Event} event
   */
  handleEnableChange(event) {
    const enableCheckbox = this.refs.enableCheckbox;
    const input =
      enableCheckbox instanceof HTMLInputElement
        ? enableCheckbox
        : event.target instanceof HTMLInputElement
          ? event.target
          : null;

    if (!input) return;

    this.#enabled = input.checked;
    this.#syncControlsEnabled();
    this.#syncPropertyFields();
    this.#dispatchStringingUpdate();
  }

  handleSelectionChange() {
    this.#syncPropertyFields();
    this.#dispatchStringingUpdate();
  }

  handleReset() {
    const {
      stringModelSelect,
      tensionMainsSelect,
      tensionCrossesSelect,
      stencilCheckbox,
    } = this.refs;

    const recommendedVariantId = this.dataset.recommendedStringVariantId;
    if (stringModelSelect && recommendedVariantId) {
      const match = Array.from(stringModelSelect.options).find(
        (option) => option.value === recommendedVariantId && !option.disabled
      );
      if (match) {
        stringModelSelect.value = recommendedVariantId;
      } else {
        const firstAvailable = Array.from(stringModelSelect.options).find((option) => !option.disabled);
        if (firstAvailable) stringModelSelect.value = firstAvailable.value;
      }
    }

    const recommendedMains = this.dataset.recommendedTensionMains;
    if (recommendedMains && tensionMainsSelect) {
      tensionMainsSelect.value = recommendedMains;
    }

    const recommendedCrosses = this.dataset.recommendedTensionCrosses;
    if (recommendedCrosses && tensionCrossesSelect) {
      tensionCrossesSelect.value = recommendedCrosses;
    }

    if (stencilCheckbox) {
      stencilCheckbox.checked = true;
    }

    this.#syncPropertyFields();
    this.#dispatchStringingUpdate();
  }

  /**
   * Cart submission payload for product-form integration.
   * @returns {StringingCartSubmission | null}
   */
  getCartSubmission() {
    if (!this.#enabled) return null;

    const properties = this.#getLineItemProperties();
    const stringVariantId = this.#getSelectedStringVariantId();

    return {
      enabled: true,
      // Non-US: add the string as a nested cart line under the racket (parent_id)
      // so it displays as a child item and decrements string inventory.
      // The cart transform returns no operations for non-US (WIL26D2C-496).
      // US: swap to the hidden Custom Strung variant. The cart transform only
      // raises that line's price. Stock inventory is wilson-inventory-sync.
      addStringLineItem: this.dataset.isUsMarket !== 'true',
      properties,
      stringVariantId: stringVariantId || undefined,
      customStrungVariantId: this.#getCustomStrungVariantId() || undefined,
      stringAddOnPrice: this.#getSelectedStringPrice(),
    };
  }

  /** @param {VariantUpdateEvent} event */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const enableCheckbox = this.refs.enableCheckbox;
    if (enableCheckbox instanceof HTMLInputElement) {
      this.#enabled = enableCheckbox.checked;
    }

    this.#updateBaseVariantData(event);

    // Defer until after product-price / product-form variant handlers replace price markup.
    queueMicrotask(() => {
      this.#syncPropertyFields();
      this.#dispatchStringingUpdate();
    });
  };

  /** @param {VariantUpdateEvent} event */
  #updateBaseVariantData(event) {
    const variantSku = event.detail.resource?.sku;
    if (typeof variantSku === 'string') {
      this.dataset.baseVariantSku = variantSku;
    }

    const variantId = event.detail.resource?.id;
    if (variantId != null) {
      this.dataset.baseVariantId = String(variantId);
    }

    const variantPrice = event.detail.resource?.price;
    if (typeof variantPrice === 'number') {
      this.dataset.baseVariantPrice = String(variantPrice);
      return;
    }

    const productPrice = event.detail.data.html.querySelector(
      `product-price[data-product-id="${this.dataset.productId}"]`
    );

    if (productPrice instanceof HTMLElement && productPrice.dataset.variantPrice != null) {
      this.dataset.baseVariantPrice = productPrice.dataset.variantPrice;
    }
  }

  #syncControlsEnabled() {
    const {
      configPanel,
      stringModelSelect,
      tensionMainsSelect,
      tensionCrossesSelect,
      stencilCheckbox,
      resetButton,
    } = this.refs;

    if (configPanel) {
      configPanel.classList.toggle('is-expanded', this.#enabled);
      configPanel.setAttribute('aria-hidden', this.#enabled ? 'false' : 'true');
      configPanel.toggleAttribute('inert', !this.#enabled);
    }

    this.#syncPanelDescendantFocusability();

    const enableCheckbox = this.refs.enableCheckbox;
    if (enableCheckbox instanceof HTMLInputElement) {
      enableCheckbox.setAttribute('aria-expanded', this.#enabled ? 'true' : 'false');
    }

    const controlsDisabled = !this.#enabled;
    for (const control of [stringModelSelect, tensionMainsSelect, tensionCrossesSelect, stencilCheckbox, resetButton]) {
      if (control) control.disabled = controlsDisabled;
    }
  }

  #syncPanelDescendantFocusability() {
    const { configPanel } = this.refs;
    if (!(configPanel instanceof HTMLElement)) return;

    const isPanelHidden = configPanel.getAttribute('aria-hidden') === 'true';
    const focusableElements = configPanel.querySelectorAll(FOCUSABLE_ELEMENTS_SELECTOR);
    for (const element of focusableElements) {
      if (!isPanelHidden) {
        if (!element.hasAttribute('data-hidden-tabindex')) continue;

        const previousTabIndex = element.getAttribute('data-hidden-tabindex');
        if (previousTabIndex == null || previousTabIndex === '') {
          element.removeAttribute('tabindex');
        } else {
          element.setAttribute('tabindex', previousTabIndex);
        }

        element.removeAttribute('data-hidden-tabindex');
        continue;
      }

      if (!element.hasAttribute('data-hidden-tabindex')) {
        element.setAttribute('data-hidden-tabindex', element.getAttribute('tabindex') || '');
      }

      element.setAttribute('tabindex', '-1');
    }
  }

  #observePanelFocusability() {
    const { configPanel } = this.refs;
    if (!(configPanel instanceof HTMLElement)) return;

    this.#panelFocusObserver?.disconnect();
    this.#panelFocusObserver = new MutationObserver(() => this.#syncPanelDescendantFocusability());
    this.#panelFocusObserver.observe(configPanel, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['aria-hidden', 'href', 'contenteditable', 'disabled'],
    });
  }

  #syncPropertyFields() {
    const propertyInputs = [
      this.refs.propertyShipWithStrings,
      this.refs.propertyStringModel,
      this.refs.propertyStringSku,
      this.refs.propertyRacketSku,
      this.refs.propertyStockRacketSku,
      this.refs.propertyTensionMains,
      this.refs.propertyTensionCrosses,
      this.refs.propertyStencilIncluded,
      this.refs.propertyStringVariantId,
      this.refs.propertyStringAddOnPriceCents,
    ];

    if (!this.#enabled) {
      for (const input of propertyInputs) {
        if (!input) continue;
        input.value = '';
        input.disabled = true;
      }
      return;
    }

    const properties = this.#getLineItemProperties();
    const stringVariantId = this.#getSelectedStringVariantId();

    if (this.refs.propertyShipWithStrings) {
      this.refs.propertyShipWithStrings.value = properties['Ship with Strings'] || '';
      this.refs.propertyShipWithStrings.disabled = false;
    }
    if (this.refs.propertyStringModel) {
      this.refs.propertyStringModel.value = properties['String Model'] || '';
      this.refs.propertyStringModel.disabled = false;
    }
    if (this.refs.propertyStringSku) {
      this.refs.propertyStringSku.value = properties['String SKU'] || '';
      this.refs.propertyStringSku.disabled = false;
    }
    if (this.refs.propertyRacketSku) {
      this.refs.propertyRacketSku.value = properties['Racket SKU'] || '';
      this.refs.propertyRacketSku.disabled = false;
    }
    if (this.refs.propertyStockRacketSku) {
      this.refs.propertyStockRacketSku.value = properties['Stock Racket SKU'] || '';
      this.refs.propertyStockRacketSku.disabled = false;
    }
    if (this.refs.propertyTensionMains) {
      this.refs.propertyTensionMains.value = properties['Tension Mains'] || '';
      this.refs.propertyTensionMains.disabled = false;
    }
    if (this.refs.propertyTensionCrosses) {
      this.refs.propertyTensionCrosses.value = properties['Tension Crosses'] || '';
      this.refs.propertyTensionCrosses.disabled = false;
    }
    if (this.refs.propertyStencilIncluded) {
      this.refs.propertyStencilIncluded.value = properties['Stencil Included'] || '';
      this.refs.propertyStencilIncluded.disabled = false;
    }
    if (this.refs.propertyStringVariantId) {
      this.refs.propertyStringVariantId.value = stringVariantId || '';
      this.refs.propertyStringVariantId.disabled = false;
    }
    if (this.refs.propertyStringAddOnPriceCents) {
      const stringAddOnPriceCents = Math.round(this.#getSelectedStringPrice());
      this.refs.propertyStringAddOnPriceCents.value =
        stringAddOnPriceCents > 0 ? String(stringAddOnPriceCents) : '';
      this.refs.propertyStringAddOnPriceCents.disabled = false;
    }
  }

  /**
   * @returns {string}
   */
  #getStringModelPropertyValue() {
    const { stringModelSelect } = this.refs;
    const selectedOption = stringModelSelect?.selectedOptions?.[0];
    const title = selectedOption?.dataset.productTitle || '';

    if (this.dataset.isUsMarket !== 'true') {
      return title;
    }

    const stringPrice = Number(selectedOption?.dataset.price) || 0;
    if (stringPrice <= 0) {
      return title;
    }

    const formattedPrice = formatMoney(
      stringPrice,
      this.dataset.moneyFormat || '{{amount}}',
      this.dataset.currency || ''
    );

    return title ? `${title} - ${formattedPrice}` : formattedPrice;
  }

  /**
   * @returns {Record<string, string>}
   */
  #getLineItemProperties() {
    const { stringModelSelect, tensionMainsSelect, tensionCrossesSelect, stencilCheckbox } = this.refs;
    const selectedOption = stringModelSelect?.selectedOptions?.[0];

    const stringAddOnPriceCents = Math.round(this.#getSelectedStringPrice());
    const stringVariantId = this.#getSelectedStringVariantId();
    const lineItemProperties = {
      'Ship with Strings': 'Yes',
      'String Model': this.#getStringModelPropertyValue(),
      'String SKU': selectedOption?.dataset.sku || '',
      'Tension Mains': tensionMainsSelect?.value || '',
      'Tension Crosses': tensionCrossesSelect?.value || '',
      'Stencil Included': stencilCheckbox?.checked ? this.#getStencilDescription() || 'Yes' : '',
      _string_variant_id: stringVariantId || '',
      '_string_add_on_price_cents': stringAddOnPriceCents > 0 ? String(stringAddOnPriceCents) : '',
    };

    const strungRacketSku = this.#getStrungRacketSku();
    if (strungRacketSku) {
      lineItemProperties['Racket SKU'] = strungRacketSku;
    }

    const stockRacketSku = this.#getStockRacketSku();
    if (stockRacketSku) {
      lineItemProperties['Stock Racket SKU'] = stockRacketSku;
    }

    return lineItemProperties;
  }

  /** @returns {string} */
  #getStrungRacketSku() {
    const customStrungVariant = this.#findCustomStrungCounterpart();
    if (customStrungVariant?.sku) return customStrungVariant.sku;
    return (this.dataset.strungRacketSku || '').trim();
  }

  /** @returns {string} */
  #getStockRacketSku() {
    return (this.dataset.baseVariantSku || '').trim();
  }

  /**
   * US add-to-cart uses the Custom Strung counterpart of the selected stock variant.
   * @returns {string}
   */
  #getCustomStrungVariantId() {
    if (this.dataset.isUsMarket !== 'true') return '';
    const counterpart = this.#findCustomStrungCounterpart();
    return counterpart?.id ? normalizeVariantId(counterpart.id) : '';
  }

  /** @returns {string} */
  #getStockVariantId() {
    return normalizeVariantId(this.dataset.baseVariantId || this.#getFormVariantId());
  }

  /** @returns {string} */
  #getFormVariantId() {
    const formId = this.dataset.productFormId;
    if (!formId) return '';
    const form = document.getElementById(formId);
    const idInput = form?.querySelector('input[name="id"]');
    return idInput instanceof HTMLInputElement ? idInput.value : '';
  }

  /**
   * @returns {{ id: string|number, sku?: string, options?: string[] } | null}
   */
  #findCustomStrungCounterpart() {
    return findCustomCounterpart(readCustomVariantMap(this, '[data-custom-strung-variants]'), this.#getStockVariantId());
  }

  /** @returns {string} */
  #getStencilDescription() {
    const rawValue = this.dataset.stencilDescription || '';
    if (!rawValue) return '';

    // Decode any HTML entities from dataset attributes before persisting to cart properties.
    const parser = document.createElement('textarea');
    parser.innerHTML = rawValue;
    return parser.value;
  }

  /** @returns {string} */
  #getSelectedStringVariantId() {
    const { stringModelSelect } = this.refs;
    const selectedOption = stringModelSelect?.selectedOptions?.[0];
    if (!selectedOption || selectedOption.disabled) return '';
    return stringModelSelect?.value || '';
  }

  /** @returns {number} */
  #getSelectedStringPrice() {
    const { stringModelSelect } = this.refs;
    const selectedOption = stringModelSelect?.selectedOptions?.[0];
    if (!selectedOption || selectedOption.disabled) return 0;
    return Number(selectedOption.dataset.price) || 0;
  }

  #ensureValidStringSelection() {
    const { stringModelSelect } = this.refs;
    if (!(stringModelSelect instanceof HTMLSelectElement)) return;

    const selectedOption = stringModelSelect.selectedOptions[0];
    if (selectedOption && !selectedOption.disabled) return;

    const recommendedVariantId = this.dataset.recommendedStringVariantId;
    const recommendedOption = recommendedVariantId
      ? Array.from(stringModelSelect.options).find(
          (option) => option.value === recommendedVariantId && !option.disabled
        )
      : undefined;
    const fallbackOption =
      recommendedOption || Array.from(stringModelSelect.options).find((option) => !option.disabled);

    if (fallbackOption) {
      stringModelSelect.value = fallbackOption.value;
    }
  }

  #dispatchStringingUpdate() {
    const baseVariantPrice = Number(this.dataset.baseVariantPrice) || 0;
    const stringAddOnPrice = this.#enabled ? this.#getSelectedStringPrice() : 0;
    const totalPrice = baseVariantPrice + stringAddOnPrice;

    this.dispatchEvent(
      new StringingUpdateEvent({
        enabled: this.#enabled,
        baseVariantPrice,
        stringAddOnPrice,
        totalPrice,
        moneyFormat: this.dataset.moneyFormat || '{{amount}}',
        currency: this.dataset.currency || '',
      })
    );
  }
}

if (!customElements.get('tennis-stringing-options-component')) {
  customElements.define('tennis-stringing-options-component', TennisStringingOptions);
}

export { TennisStringingOptions };
